import { FC } from 'react';

import {
  LifecycleConfirmDialog,
  LifecycleStatus,
  LifecycleStatusTag,
  getLifecycleStatusLabel,
  getManualLifecycleTransitions,
} from '@eluve/blocks';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuSelectableItem,
  DropdownMenuTrigger,
  Icon,
} from '@eluve/components';
import { useTranslation } from '@eluve/i18n';

import { useLifecycleStatusActions } from './useLifecycleStatusActions';

type LifecycleStatusCellProps = {
  tenantId: string;
  /** Null for invite rows, which have no `tenant_users` row to edit. */
  userId: string | null | undefined;
  status: LifecycleStatus | null | undefined;
  isExcluded?: boolean;
  /** Shown in the churn confirmation prompt (e.g. the user's email). */
  subjectLabel?: string;
  onChanged: () => void;
};

/**
 * Inline-editable Status cell for both Users pages (ENG-4873): the lifecycle
 * tag itself is the dropdown trigger. Excluded users and invite rows (no
 * status) render the static tag, so the write hook is scoped to the
 * interactive inner component and never fires for non-editable rows.
 */
export const LifecycleStatusCell: FC<LifecycleStatusCellProps> = ({
  status,
  userId,
  isExcluded = false,
  ...interactiveProps
}) => {
  if (isExcluded || !status || !userId) {
    return <LifecycleStatusTag status={status} isExcluded={isExcluded} />;
  }
  return (
    <InteractiveLifecycleStatusCell
      userId={userId}
      currentStatus={status}
      {...interactiveProps}
    />
  );
};

type InteractiveLifecycleStatusCellProps = {
  tenantId: string;
  userId: string;
  currentStatus: LifecycleStatus;
  subjectLabel?: string;
  onChanged: () => void;
};

const InteractiveLifecycleStatusCell: FC<
  InteractiveLifecycleStatusCellProps
> = ({ tenantId, userId, currentStatus, subjectLabel, onChanged }) => {
  const { t } = useTranslation();
  const {
    selectTransition,
    confirmTarget,
    isConfirmOpen,
    confirm,
    cancel,
    isPending,
  } = useLifecycleStatusActions({ tenantId, userId, currentStatus, onChanged });

  const options = [
    currentStatus,
    ...getManualLifecycleTransitions(currentStatus),
  ];

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            disabled={isPending}
            aria-label={t('smartBlocks.lifecycleStatus.changeStatusAria', {
              status: getLifecycleStatusLabel(currentStatus),
            })}
            className="inline-flex items-center rounded-md outline-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <LifecycleStatusTag status={currentStatus}>
              <Icon name="ChevronDown" size="xs" />
            </LifecycleStatusTag>
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          {options.map((status) => (
            <DropdownMenuSelectableItem
              key={status}
              currentValue={currentStatus}
              value={status}
              text={getLifecycleStatusLabel(status)}
              onClick={(value) => selectTransition(value as LifecycleStatus)}
            />
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
      <LifecycleConfirmDialog
        open={isConfirmOpen}
        target={confirmTarget}
        subjectLabel={subjectLabel}
        isPending={isPending}
        onConfirm={confirm}
        onCancel={cancel}
      />
    </>
  );
};
